import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';

const steps = [
  {
    number: '01',
    icon: 'MousePointerClick',
    title: 'Build your form',
    description: 'Drag fields onto the canvas, set up conditional logic, configure multi-step flows, and apply your brand theme — all in one editor.',
    color: 'text-teal-600',
    bg: 'bg-teal-50',
    border: 'border-teal-200',
  },
  {
    number: '02',
    icon: 'Globe',
    title: 'Publish in one click',
    description: 'Get a shareable link instantly, or embed your form inline, as a popup, or slide-in on any website. Your form is live in seconds.',
    color: 'text-blue-600',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
  },
  {
    number: '03',
    icon: 'Inbox',
    title: 'Collect & automate',
    description: 'Submissions flow into your dashboard in real time. Connect your forms to webhooks and push data to your CRM when you need it.',
    color: 'text-orange-600',
    bg: 'bg-orange-50',
    border: 'border-orange-200',
  },
  {
    number: '04',
    icon: 'TrendingUp',
    title: 'Optimize with data',
    description: 'Analyze conversion funnels, run A/B tests, and see exactly where users drop off. Make data-driven decisions to maximize results.',
    color: 'text-purple-600',
    bg: 'bg-purple-50',
    border: 'border-purple-200',
  },
];

const WorkflowSection = () => {
  return (
    <section id="how-it-works" className="py-24 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-teal-50 border border-teal-200 rounded-full text-xs font-semibold text-teal-700 mb-5">
            <Icon name="Workflow" size={13} />
            <span>How it works</span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-heading font-bold text-gray-900 leading-tight mb-4">
            From idea to live form in under 5 minutes
          </h2>
          <p className="text-lg text-gray-500 leading-relaxed">
            No setup fees. No developer needed. Just a powerful platform that gets out of your way.
          </p>
        </div>

        {/* Steps — asymmetric layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-16">
          {steps?.map((step, index) => (
            <div
              key={step?.number}
              className={`relative flex gap-6 p-8 rounded-2xl border ${step?.border} ${step?.bg} hover:shadow-md transition-all duration-300 group`}
            >
              {/* Number */}
              <div className="flex-shrink-0">
                <div className={`w-12 h-12 rounded-xl ${step?.bg} border ${step?.border} flex items-center justify-center`}>
                  <Icon name={step?.icon} size={22} className={step?.color} />
                </div>
              </div>
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <span className={`text-xs font-bold font-mono ${step?.color} opacity-60`}>{step?.number}</span>
                  <h3 className="text-xl font-heading font-bold text-gray-900">{step?.title}</h3>
                </div>
                <p className="text-gray-500 text-sm leading-relaxed">{step?.description}</p>
              </div>
              {/* Connector arrow for non-last items */}
              {index < steps?.length - 1 && (
                <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 z-10 hidden lg:block">
                  {index % 2 === 1 && (
                    <div className="w-8 h-8 bg-white border border-gray-200 rounded-full flex items-center justify-center shadow-sm">
                      <Icon name="ArrowDown" size={14} className="text-gray-400" />
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Bottom CTA banner */}
        <div className="relative rounded-3xl overflow-hidden bg-[#0a1628] p-10 lg:p-16 text-center">
          <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: 'radial-gradient(circle at 30% 50%, #0e7572 0%, transparent 60%), radial-gradient(circle at 70% 50%, #F97316 0%, transparent 60%)'
          }} />
          <div className="relative">
            <h3 className="text-3xl lg:text-4xl font-heading font-bold text-white mb-4">
              Ready to take control?
            </h3>
            <p className="text-gray-400 text-lg mb-8 max-w-xl mx-auto">
              Replace scattered form tools with one place to build, publish, analyze, and improve.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/authentication"
                className="flex items-center gap-2 px-8 py-4 bg-[#0e7572] hover:bg-[#0a5f5c] text-white font-semibold text-base rounded-xl transition-all duration-200 shadow-lg shadow-teal-900/40 hover:shadow-xl hover:-translate-y-0.5"
              >
                <span>Start free — no card needed</span>
                <Icon name="ArrowRight" size={18} />
              </Link>
              <Link
                to="/templates-gallery"
                className="flex items-center gap-2 px-8 py-4 border border-white/15 hover:border-white/30 text-white/80 hover:text-white font-semibold text-base rounded-xl transition-all duration-200 bg-white/5 hover:bg-white/10"
              >
                <Icon name="LayoutTemplate" size={18} />
                <span>Browse templates</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WorkflowSection;
